import { useEffect } from 'react';
import SelectField from '../../../components/Forms/Input/SelectField';
import TextAreaField from '../../../components/Forms/Input/TextAreaField';
import { useBasePurchase } from '../../../hooks/purchases/useBasePurchase';
import { usePaymentCondition } from '../../../hooks/usePaymentCondition';
import { useProductSettlement } from '../../../hooks/settlement/useProductSettlement';
import { useAllowedOptions } from '../../../hooks/settings/useAllowedOptions';
import { useTerm } from '../../../hooks/term/useTerm';
import PurchaseCreditDaysSection from '../paymentConditionSection/PurchaseCreditDaysSection';
import PurchaseCreditMonthsSection from '../paymentConditionSection/PurchaseCreditMonthsSection';
import PurchaseCreditYearsSection from '../paymentConditionSection/PurchaseCreditYearsSection';
import PurchasePaymentMethodSelector from '../paymentMethod/PurchasePaymentMethodSelector';
import { DOCUMENT_TYPE_CODES } from '../../../constants/documentTypeCodes';

const PaymentConditionSection = () => {
  const {
    form,
    updateField,
    handlerFinalizePurchase,
    purchaseSettlementLoaded,
    setPaymentConditionCodeFn,
  } = useBasePurchase();

  const { fetchPaymentConditionDropdown, paymentConditionDropdown } =
    usePaymentCondition();
  const { handlerFinalizeSettlement } = useProductSettlement();
  const { getCodes, fetchCodesByModuleAndTarget } = useAllowedOptions();
  const { termDropdown, fetchTermDropdown } = useTerm();

  useEffect(() => {
    fetchCodesByModuleAndTarget('PURCHASES', 'PAYMENT_CONDITION');
    fetchPaymentConditionDropdown();
    fetchTermDropdown();
  }, []);

  const allowedPaymentCodes = getCodes('PURCHASES', 'PAYMENT_CONDITION');

  const filteredPaymentConditions = paymentConditionDropdown.filter((item) =>
    allowedPaymentCodes.includes(item.code)
  );

  const documentCode = form.documentType?.code;
  const isCreditNote = documentCode === DOCUMENT_TYPE_CODES.CREDIT_NOTE;
  const isDebitNote = documentCode === DOCUMENT_TYPE_CODES.DEBIT_NOTE;
  // const isTaxCredit = documentCode === DOCUMENT_TYPE_CODES.TAX_CREDIT;
  const isExcludedSubject =
    documentCode === DOCUMENT_TYPE_CODES.EXCLUDED_SUBJECT;

  const selectedCondition = filteredPaymentConditions.find(
    (p) => String(p.id) === String(form.paymentCondition?.id)
  );

  const isCredit = selectedCondition?.code === 'CREDITO';

  if (isCreditNote || isDebitNote) {
    return (
      <div className="mt-6 w-full rounded-sm border border-stroke bg-white p-6.5 shadow-default dark:border-strokedark dark:bg-boxdark">
        <p className="pb-2 text-sm font-medium text-black dark:text-white">
          FECHA DEL DOCUMENTO:{' '}
          <span className="font-bold">
            {form.purchaseDate || 'Fecha no disponible'}
          </span>
        </p>

        <TextAreaField
          name="comments"
          label="Comentarios (Uso Interno)"
          placeholder="Comentarios/Observaciones"
          value={form.comments || ''}
          onChange={(e) => updateField('comments', e.target.value)}
          iconKey="edit"
        />

        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={handlerFinalizePurchase}
            className="rounded bg-primary px-4 py-2 text-white hover:bg-opacity-90"
          >
            Finalizar Compra
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-6 w-full rounded-sm border border-stroke bg-white p-6.5 shadow-default dark:border-strokedark dark:bg-boxdark">
      {/* Fila: Condición de pago + Unidad del plazo */}

      <div className="mb-4">
        <h4 className="text-md font-semibold text-black dark:text-white">
          Configuración de la Condición de Pago
        </h4>
        <p className="text-gray-600 dark:text-gray-400 text-sm">
          Selecciona si esta compra será de contado o a crédito. Si eliges
          crédito, deberás indicar la unidad de plazo (días, meses o años) y su
          respectivo valor.
        </p>
      </div>

      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
        {/* 🟩 Condición de Pago */}
        <div>
          <SelectField
            name="paymentCondition"
            label="Condición de Pago"
            iconKey="payment"
            value={form.paymentCondition?.id || ''}
            disabled={purchaseSettlementLoaded}
            onChange={(e) => {
              const selected = filteredPaymentConditions.find(
                (opt) => String(opt.id) === String(e.target.value)
              );

              if (selected) {
                updateField('paymentCondition', {
                  id: selected.id,
                  name: selected.name,
                  code: selected.code,
                  termId: undefined,
                  termName: undefined,
                  termCode: undefined,
                  creditDays: undefined,
                  creditMonths: undefined,
                  creditYears: undefined,
                  creditDueDate: undefined,
                });

                setPaymentConditionCodeFn(selected.code);
              }
            }}
            options={filteredPaymentConditions.map((item) => ({
              value: item.id,
              label: item.name,
            }))}
          />
        </div>

        {/* 🟦 Unidad del Plazo (solo si es crédito) */}
        {isCredit && (
          <div>
            <SelectField
              name="paymentCondition.term"
              label="Unidad del Plazo"
              iconKey="calendar"
              value={form.paymentCondition?.termId || ''}
              onChange={(e) => {
                const selectedTerm = termDropdown.find(
                  (opt) => String(opt.id) === String(e.target.value)
                );

                if (selectedTerm) {
                  // 🚀 Reiniciar los campos de plazo
                  updateField('paymentCondition', {
                    ...form.paymentCondition,
                    termId: selectedTerm.id,
                    termName: selectedTerm.name,
                    termCode: selectedTerm.code,

                    // ✅ Limpiar todos los campos de valores de crédito
                    creditDays: undefined,
                    creditMonths: undefined,
                    creditYears: undefined,
                    creditDueDate: undefined, // también limpiamos la fecha
                  });
                } else {
                  // Si por alguna razón se selecciona algo inválido, también limpiamos todo
                  updateField('paymentCondition', {
                    ...form.paymentCondition,
                    termId: undefined,
                    termName: undefined,
                    creditDays: undefined,
                    creditMonths: undefined,
                    creditYears: undefined,
                    creditDueDate: undefined,
                  });
                }
              }}
              disabled={purchaseSettlementLoaded}
              options={termDropdown.map((item) => ({
                value: item.id,
                label: item.name,
              }))}
            />
          </div>
        )}
      </div>

      {/* Fila: Campos de crédito según unidad */}
      {isCredit && (
        <div className="mb-6 grid gap-4 sm:grid-cols-2 md:grid-cols-2">
          {form.paymentCondition?.termCode === '01' && (
            <PurchaseCreditDaysSection />
          )}
          {form.paymentCondition?.termCode === '02' && (
            <PurchaseCreditMonthsSection />
          )}
          {form.paymentCondition?.termCode === '03' && (
            <PurchaseCreditYearsSection />
          )}
        </div>
      )}

      {/* 🔹 Selector de método de pago SOLO para Sujeto Excluido */}
      {isExcludedSubject && (
        <div className="mb-6">
          <PurchasePaymentMethodSelector />
        </div>
      )}

      {/* Fecha del documento */}
      <p className="pb-2 text-sm font-medium text-black dark:text-white">
        FECHA DEL DOCUMENTO:{' '}
        <span className="font-bold">
          {form.purchaseDate || 'Fecha no disponible'}
        </span>
      </p>

      {/* Comentarios internos */}
      <TextAreaField
        name="comments"
        label="Comentarios (Uso Interno)"
        placeholder="Comentarios/Observaciones"
        value={form.comments || ''}
        disabled={purchaseSettlementLoaded}
        onChange={(e) => updateField('comments', e.target.value)}
        iconKey="edit"
      />

      {/* Acciones */}
      <div className="mt-4 flex justify-end">
        {purchaseSettlementLoaded ? (
          <button
            type="button"
            onClick={handlerFinalizeSettlement}
            className="rounded bg-primary px-4 py-2 text-white hover:bg-opacity-90"
          >
            Asentar Productos
          </button>
        ) : (
          <button
            type="button"
            onClick={handlerFinalizePurchase}
            className="flex items-center justify-center gap-2 rounded bg-primary px-4.5 py-2.5 font-medium text-white hover:bg-opacity-90"
          >
            Finalizar Compra
          </button>
        )}
      </div>
    </div>
  );
};

export default PaymentConditionSection;
